import { useMemo, useRef, useState } from 'react';
import { useAuth, type Agent } from '@wa/shared';

const MAX = 2000;

/**
 * Writing an internal note.
 *
 * Everything about it is deliberately unlike the reply composer — amber ground,
 * a standing label, and a button that says "Add note" rather than "Send" — the
 * word the reference uses, and it keeps the property that matters: whatever else
 * this button says, it never says Send.
 * Confusing the two here does not break a page, it tells a customer something
 * that was written about them, so the two modes are made hard to mistake rather
 * than merely distinct.
 */
export function NoteComposer({ agents, isSaving, onSave }: {
  agents: Agent[];
  isSaving: boolean;
  onSave: (body: string, mentionedAgentIds: number[]) => void;
}) {
  const { user } = useAuth();
  const [draft, setDraft] = useState('');
  const [caret, setCaret] = useState(0);
  const box = useRef<HTMLTextAreaElement>(null);

  // Yourself is not offered. The server drops a self-mention as the typo it is,
  // so listing your own name would let someone pick it and see nothing happen.
  const others = useMemo(() => agents.filter((a) => a.id !== user?.id), [agents, user?.id]);

  // The @token immediately before the caret, if the agent is mid-mention.
  const token = useMemo(() => {
    const upto = draft.slice(0, caret);
    const at = upto.lastIndexOf('@');
    if (at === -1) return null;

    const fragment = upto.slice(at + 1);
    // A space ends a mention only once a name has started, so "@ " is not a match
    // but "@Priya Sh" still is — people's names have spaces in them.
    if (fragment.includes('\n') || fragment.length > 24) return null;

    return { at, fragment };
  }, [draft, caret]);

  const suggestions = useMemo(() => {
    if (!token) return [];
    const needle = token.fragment.toLowerCase();
    return others
      .filter((a) => a.displayName.toLowerCase().includes(needle))
      .slice(0, 5);
  }, [token, others]);

  /**
   * Mentions are resolved from the text at save time, not tracked as the agent
   * types. Someone who deletes half a name has un-mentioned that person, and
   * anything that remembers the click would notify them anyway.
   */
  const mentioned = useMemo(() => {
    const found = new Set<number>();
    for (const agent of others) {
      if (draft.includes(`@${agent.displayName}`)) found.add(agent.id);
    }
    return [...found];
  }, [draft, others]);

  function insert(agent: Agent) {
    if (!token) return;

    const before = draft.slice(0, token.at);
    const after = draft.slice(caret);
    const next = `${before}@${agent.displayName} ${after}`;
    const position = before.length + agent.displayName.length + 2;

    setDraft(next);

    queueMicrotask(() => {
      box.current?.focus();
      box.current?.setSelectionRange(position, position);
      setCaret(position);
    });
  }

  function save() {
    const body = draft.trim();
    if (!body || isSaving || body.length > MAX) return;
    onSave(body, mentioned);
    setDraft('');
  }

  const names = others.filter((a) => mentioned.includes(a.id)).map((a) => a.displayName);

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-2">
      <div className="relative rounded-lg border border-(--color-note-line) bg-(--color-note) p-2">
        <p className="mb-1.5 px-0.5 text-micro font-semibold tracking-wide text-(--color-note-ink) uppercase">
          Internal note — the customer never sees this
        </p>

        <textarea
          ref={box}
          rows={2}
          value={draft}
          maxLength={MAX}
          onChange={(e) => { setDraft(e.target.value); setCaret(e.target.selectionStart); }}
          onKeyUp={(e) => setCaret(e.currentTarget.selectionStart)}
          onClick={(e) => setCaret(e.currentTarget.selectionStart)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && suggestions.length === 0) {
              e.preventDefault();
              save();
            }
            if (e.key === 'Enter' && suggestions.length > 0) {
              e.preventDefault();
              insert(suggestions[0]!);
            }
          }}
          placeholder="Something for the team. Type @ to notify someone."
          aria-label="Internal note"
          className="tap max-h-40 min-h-11 w-full resize-y rounded border border-(--color-note-line) bg-(--color-surface) px-2.5 py-2 text-body text-(--color-ink) outline-none focus:border-(--color-note-strong)"
        />

        {suggestions.length > 0 && (
          <ul className="absolute bottom-full left-2 z-10 mb-1 w-[min(16rem,80%)] overflow-hidden rounded-lg border border-(--color-line) bg-(--color-surface) shadow-lg">
            {suggestions.map((agent, index) => (
              <li key={agent.id}>
                <button
                  type="button"
                  onClick={() => insert(agent)}
                  className={`tap w-full px-3 py-2 text-left text-small hover:bg-(--color-sunken) ${
                    index === 0 ? 'bg-(--color-sunken)' : ''
                  }`}
                >
                  {agent.displayName}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <p className="min-w-0 flex-1 text-micro text-(--color-muted)">
          {names.length > 0
            ? `Will notify ${names.join(', ')}`
            : 'Nobody will be notified.'}
        </p>

        <span className={`shrink-0 text-micro tabular-nums ${
          draft.length > MAX * 0.9 ? 'text-(--color-bad)' : 'text-(--color-muted)'
        }`}>
          {draft.length}/{MAX}
        </span>

        <button
          type="button"
          onClick={save}
          disabled={isSaving || !draft.trim()}
          className="tap shrink-0 rounded-lg bg-(--color-note-strong) px-4 py-2 text-body font-medium text-(--color-note) disabled:opacity-40"
        >
          {isSaving ? 'Saving…' : 'Add note'}
        </button>
      </div>
    </div>
  );
}
